import React, { useState, useEffect } from 'react';
import { Target, Sparkles, X, CheckCircle, SkipForward } from 'lucide-react';
import toast from 'react-hot-toast';
import { getKeywordType, analyzeJobDescription } from '../../../utils/aiResumeEngine';

const ScoreRing = ({ score }) => {
  const r = 32, circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg width="80" height="80" className="-rotate-90">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#e5e7eb" strokeWidth="7" />
        <circle
          cx="40" cy="40" r={r} fill="none"
          stroke={color} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4,0,0.2,1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-black" style={{ color }}>{score}</span>
        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">match</span>
      </div>
    </div>
  );
};

export const JobTargetFlow = ({
  isOpen,
  onClose,
  onComplete,
  analyzeJobDescriptionAsync,
  mode = 'pre-launch',
  initialJdText = '',
  cachedAnalysis = null,
}) => {
  const [step, setStep] = useState(1);
  const [hasDraft] = useState(() => !!localStorage.getItem('qa_resume_builder_v3'));
  const [jdText, setJdText] = useState(initialJdText);
  const [inputType, setInputType] = useState('text');
  const [jdUrl, setJdUrl] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [analysis, setAnalysis] = useState(cachedAnalysis);
  const [checkedKeywords, setCheckedKeywords] = useState({});
  const [analysing, setAnalysing] = useState(false);
  const textareaRef = React.useRef(null);

  useEffect(() => {
    if (isOpen) {
      setStep(1);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && step === 1) {
      setTimeout(() => textareaRef.current?.focus(), 120);
    }
  }, [isOpen, step]);

  if (!isOpen) return null;

  const handleAnalyse = async (textParam = null) => {
    const textToAnalyze = typeof textParam === 'string' ? textParam : jdText;
    if (!textToAnalyze.trim()) return;
    setAnalysing(true);
    setErrorMsg('');
    try {
      let result;
      if (analyzeJobDescriptionAsync) {
        result = await analyzeJobDescriptionAsync(textToAnalyze, {});
      } else {
        result = analyzeJobDescription(textToAnalyze, {});
      }
      setAnalysis(result);
      const defaults = {};
      (result?.uniqMissing || []).forEach(k => { defaults[k] = true; });
      setCheckedKeywords(defaults);
      setStep(2);
    } catch (err) {
      console.error('[JobTargetFlow] Analysis failed:', err);
      if (err.message === 'LIMIT_EXCEEDED') {
        toast.error('Daily free AI limit reached! Using smart fallback extraction.', { duration: 5000, position: 'top-center' });
      } else {
        toast.error('AI analysis failed. Using smart fallback extraction.', { duration: 4000, position: 'top-center' });
      }
      const result = analyzeJobDescription(textToAnalyze, {});
      setAnalysis(result);
      const defaults = {};
      (result?.uniqMissing || []).forEach(k => { defaults[k] = true; });
      setCheckedKeywords(defaults);
      setStep(2);
    } finally {
      setAnalysing(false);
    }
  };

  const handleFetchUrl = async () => {
    if (!jdUrl.trim()) return;
    setExtracting(true);
    setErrorMsg('');
    try {
      const res = await fetch('/api/resume-router?action=scrape-jd', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: jdUrl.trim() })
      });
      const data = await res.json();
      if (!res.ok) {
        setErrorMsg(data.error || 'Failed to extract JD. Please paste text manually.');
        setInputType('text');
      } else {
        setJdText(data.text);
        await handleAnalyse(data.text);
      }
    } catch (err) {
      setErrorMsg('Network error. Please paste text manually.');
      setInputType('text');
    } finally {
      setExtracting(false);
    }
  };

  const toggleKw = (kw) =>
    setCheckedKeywords(prev => ({ ...prev, [kw]: !prev[kw] }));

  const selectedCount = Object.values(checkedKeywords).filter(Boolean).length;
  const totalMissing  = (analysis?.uniqMissing || []).length;

  const sortedMissing = [...(analysis?.uniqMissing || [])].sort((a, b) => {
    const typeA = getKeywordType(a);
    const typeB = getKeywordType(b);
    if (typeA === 'tool' && typeB !== 'tool') return -1;
    if (typeA !== 'tool' && typeB === 'tool') return 1;
    return a.localeCompare(b);
  });

  const handleLaunch = (startFresh = false) => {
    const approved = (analysis?.uniqMissing || []).filter(k => checkedKeywords[k]);
    onComplete(jdText, approved, analysis, startFresh);
  };

  const specLabel = analysis?.detectedSpecId
    ? ({ manual:'Manual QA', automation:'Automation QA', sdet:'SDET', performance:'Performance', security:'Security', mobile:'Mobile QA', ai:'AI Testing' }[analysis.detectedSpecId] || 'QA Engineer')
    : 'QA Engineer';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden max-h-[92vh] flex flex-col relative">
        {mode === 'mid-session' && (
          <button
            onClick={onClose}
            disabled={analysing || extracting}
            className="absolute top-4 right-4 p-2 rounded-xl hover:bg-gray-100 transition-colors z-20 disabled:opacity-50 text-gray-500"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        )}

        <div className="h-1 flex flex-shrink-0">
          <div className={`h-full transition-all duration-500 bg-gradient-to-r from-blue-500 via-violet-500 to-indigo-500 ${step === 1 ? 'w-1/2' : 'w-full'}`} />
        </div>

        <div className="overflow-y-auto flex-1">
          {step === 1 && (
            <div className="p-6 sm:p-8 pt-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-200">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-black text-gray-900">Target a Job — We'll Tailor Your Resume</h2>
                  <p className="text-xs text-gray-500">Paste any JD below. AI auto-fills skills, summary & match score — free.</p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Step 1 of 2</span>
                <span className="text-[10px] text-gray-400">Paste → Smart Preview → Apply</span>
              </div>

              <div className="flex bg-gray-100 p-1 rounded-xl mb-4 w-full max-w-[240px]">
                <button
                  onClick={() => setInputType('text')}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${
                    inputType === 'text' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Paste Text
                </button>
                <button
                  onClick={() => setInputType('url')}
                  className={`flex-1 py-1.5 text-xs font-bold rounded-lg transition-all ${
                    inputType === 'url' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Paste URL
                </button>
              </div>

              {errorMsg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 text-red-600 text-xs rounded-xl flex items-start gap-2 animate-fadeIn">
                  <span className="mt-0.5">⚠️</span>
                  <p>{errorMsg}</p>
                </div>
              )}

              {inputType === 'text' ? (
                <textarea
                  ref={textareaRef}
                  value={jdText}
                  onChange={e => setJdText(e.target.value)}
                  rows={6}
                  placeholder={`Paste the full job description here…`}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all resize-none placeholder-gray-400 leading-relaxed"
                  style={{ fontSize: '15px' }}
                />
              ) : (
                <input
                  type="url"
                  value={jdUrl}
                  onChange={e => setJdUrl(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleFetchUrl()}
                  placeholder="https://www.linkedin.com/jobs/view/..."
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-2xl text-sm outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all placeholder-gray-400"
                  style={{ fontSize: '15px' }}
                />
              )}

              <div className="flex items-center gap-3 mt-4">
                {inputType === 'text' ? (
                  <button
                    onClick={() => handleAnalyse()}
                    disabled={!jdText.trim() || analysing}
                    className="flex-1 flex items-center justify-center gap-2 py-3 px-5 bg-gradient-to-r from-blue-600 to-violet-600 text-white font-bold rounded-2xl hover:from-blue-700 hover:to-violet-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-blue-200 min-h-[48px]"
                  >
                    {analysing ? (
                      <><span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" /> Analysing…</>
                    ) : (
                      <><Sparkles className="w-4 h-4" /> Analyse & Preview Resume →</>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleFetchUrl}
                    disabled={!jdUrl.trim() || extracting || analysing}
                    className="flex-1 flex items-center justify-center gap-2 py-3 px-5 bg-gradient-to-r from-blue-600 to-violet-600 text-white font-bold rounded-2xl hover:from-blue-700 hover:to-violet-700 active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-blue-200 min-h-[48px]"
                  >
                    {extracting || analysing ? (
                      <><span className="animate-spin inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full" /> {extracting ? 'Extracting…' : 'Analysing…'}</>
                    ) : (
                      <><Sparkles className="w-4 h-4" /> Fetch & Auto-Analyse →</>
                    )}
                  </button>
                )}
                
                {mode === 'pre-launch' ? (
                  <button
                    onClick={() => onClose()}
                    className="flex items-center gap-1.5 py-3 px-4 text-sm text-gray-500 font-semibold hover:bg-gray-100 rounded-2xl transition-colors min-h-[48px]"
                  >
                    <SkipForward className="w-4 h-4" /> Skip
                  </button>
                ) : (
                  analysis && (
                    <button
                      onClick={() => setStep(2)}
                      className="py-3 px-4 text-sm text-blue-600 font-semibold hover:underline flex-shrink-0"
                    >
                      View Current Analysis →
                    </button>
                  )
                )}
              </div>
              <p className="text-center text-[10px] text-gray-400 mt-3">
                {inputType === 'text' ? '🔒 Analysis runs entirely in your browser — your JD is never uploaded' : '🌍 Extracted on our secure servers, analyzed in your browser'}
              </p>
            </div>
          )}

          {step === 2 && analysis && (
            <div className="p-6 sm:p-7">
              <div className="sticky top-0 bg-white/95 backdrop-blur-sm pt-6 sm:pt-7 pb-4 px-6 sm:px-7 -mx-6 sm:-mx-7 -mt-6 sm:-mt-7 border-b border-gray-100 z-20 shadow-[0_10px_15px_-3px_rgba(255,255,255,0.8)] mb-6">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-200">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-gray-900">Your Smart Resume Preview</h2>
                    <p className="text-xs text-gray-500">Review what gets added — uncheck anything you don't want</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">Step 2 of 2</span>
                  <span className="text-[10px] text-gray-400">{selectedCount} of {totalMissing} keywords selected</span>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-gradient-to-br from-gray-50 to-blue-50/40 rounded-2xl border border-gray-100 mb-4">
                <ScoreRing score={analysis.matchScore || 0} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-1.5 mb-1">
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full">
                      🎯 {specLabel}
                    </span>
                    {analysis.seniority && (
                      <span className="text-[11px] font-semibold bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full capitalize">
                        {analysis.seniority} level
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    <strong className="text-gray-700">{analysis.matched?.length || 0}</strong> keywords already on your profile ·{' '}
                    <strong className="text-violet-700">{totalMissing}</strong> will be added
                  </p>
                </div>
              </div>

              {totalMissing > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">⚡ Keywords to add</p>
                    <div className="flex gap-2">
                      <button onClick={() => { const all = {}; (analysis.uniqMissing||[]).forEach(k => { all[k] = true; }); setCheckedKeywords(all); }} className="text-[10px] font-bold text-blue-600 hover:underline">All</button>
                      <span className="text-gray-300">|</span>
                      <button onClick={() => setCheckedKeywords({})} className="text-[10px] font-bold text-gray-400 hover:underline">None</button>
                    </div>
                  </div>
                  <div className="border border-gray-100 rounded-2xl overflow-hidden divide-y divide-gray-50 max-h-48 overflow-y-auto">
                    {sortedMissing.slice(0, 100).map(kw => (
                      <label key={kw} className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50/50 cursor-pointer transition-colors">
                        <input type="checkbox" checked={!!checkedKeywords[kw]} onChange={() => toggleKw(kw)} className="w-4 h-4 rounded accent-blue-600 flex-shrink-0" />
                        <span className="text-sm text-gray-800 font-medium flex-1">{kw}</span>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${getKeywordType(kw) === 'tool' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-green-50 text-green-700 border-green-100'}`}>{getKeywordType(kw)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 mb-6 text-xs text-indigo-800 space-y-1">
                <div className="flex items-center gap-1.5"><CheckCircle className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" /><span>Selected keywords auto-routed to sections</span></div>
                <div className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" /><span>Summary generated & tailored</span></div>
                <div className="flex items-center gap-1.5"><Target className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" /><span>JD saved — next visit auto-loads your draft</span></div>
              </div>

              <div className="sticky bottom-0 bg-white/95 backdrop-blur-sm pt-4 pb-6 px-6 sm:px-7 -mx-6 sm:-mx-7 -mb-6 sm:-mb-7 border-t border-gray-100 flex items-center gap-3 z-20 shadow-[0_-10px_15px_-3px_rgba(255,255,255,0.8)]">
                {mode === 'pre-launch' && hasDraft ? (
                  <>
                    <button 
                      onClick={() => {
                        if (window.confirm("Are you sure you want to start fresh? Your current draft will be permanently deleted.")) {
                          handleLaunch(true);
                        }
                      }} 
                      className="py-3.5 px-5 text-sm font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-2xl transition-colors min-h-[48px]"
                    >
                      Start Fresh
                    </button>
                    <button onClick={() => handleLaunch(false)} className="flex-1 flex items-center justify-center gap-2 py-3.5 px-5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold rounded-2xl hover:from-violet-700 hover:to-indigo-700 active:scale-95 transition-all shadow-lg shadow-violet-200 min-h-[48px] text-sm">
                      <CheckCircle className="w-4 h-4" /> Merge with Current Resume →
                    </button>
                  </>
                ) : (
                  <button onClick={() => handleLaunch(false)} className="flex-1 flex items-center justify-center gap-2 py-3.5 px-5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-bold rounded-2xl hover:from-violet-700 hover:to-indigo-700 active:scale-95 transition-all shadow-lg shadow-violet-200 min-h-[48px] text-sm">
                    <CheckCircle className="w-4 h-4" /> {mode === 'pre-launch' ? 'Apply & Build My Resume →' : 'Apply & Auto-fill Resume →'}
                  </button>
                )}
                <button onClick={() => setStep(1)} className="py-3 px-4 text-sm text-gray-500 font-semibold hover:bg-gray-100 rounded-2xl transition-colors min-h-[48px] flex-shrink-0">← Back</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
